/**
 * Returns a function that runs async tasks with at most `maxConcurrent` in flight at once. Unlike batching, a queued
 * task starts as soon as any running task settles, so callers see results stream in rather than arrive in waves.
 *
 * Passing a `signal` lets a queued task be dropped when it's aborted, so cancelled work doesn't hold up the queue.
 */
export function createConcurrencyLimiter(maxConcurrent: number) {
  const limit = Math.max(1, maxConcurrent);
  const queue: Array<() => void> = [];
  let active = 0;

  const next = () => {
    while (active < limit && queue.length > 0) {
      queue.shift()();
    }
  };

  return function runLimited<T>(task: () => Promise<T>, signal?: AbortSignal): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      if (signal?.aborted) {
        reject(signal.reason);
        return;
      }

      const onAbort = () => {
        const index = queue.indexOf(start);
        if (index !== -1) {
          queue.splice(index, 1);
          reject(signal.reason);
        }
      };

      function start() {
        signal?.removeEventListener('abort', onAbort);
        active += 1;
        // chained off a resolved promise so a task that throws synchronously still settles and frees its slot
        Promise.resolve()
          .then(task)
          .then(resolve, reject)
          .finally(() => {
            active -= 1;
            next();
          });
      }

      signal?.addEventListener('abort', onAbort, { once: true });
      queue.push(start);
      next();
    });
  };
}
